import { TiptapCollabProvider } from '@tiptap-pro/provider';
import { NxtYDocConfig } from 'components/RichTextEditor/hooks/useCreateEditor';
import { NXT_LOCK_ORIGIN } from 'providers/contexts/extensions/Editorial';
import { clone, mergeDeep } from 'remeda';
import { AssetType } from '../../../../utility/types/cosmosTypes';
import {
  FullEditorialAsset,
  GetEditorialAssetIdLockResult,
  GetUserMeData,
  UserAccountSummary,
} from '../../../../utility/types/dataProvider';
import { getAuthorsUuidArray } from '../components/common/Authors';
import { cleanCustomProperties } from '../components/common/CustomProperties/hooks/useCustomProperties';
import { GlobalSettingsType, SiteSettingsType } from 'providers/contexts/SettingsContext';
import { Workflow } from 'providers/contexts/extensions/Workflows';
import { normalizeWorkflowId } from 'utility/workflowUtils';
import { AssetQuickLaunchPrefill } from 'components/editorial/AssetQuickLaunch/types';
import dayjs from 'dayjs';
import { useFormContext } from 'react-hook-form';
import { AssetEditorsContextValuesType } from './AssetEditorsContextProvider';
import { AssetConfigContextValuesType } from './AssetConfigContextProvider';

/**
 * -----------------------------------------------------------------
 * Helper Functions / Constants / Types
 * -----------------------------------------------------------------
 */

/**
 * Asset Form Values Type
 **/
export type AssetFormValuesType = Partial<FullEditorialAsset>;

// undefined = disabled/undetermined, instance of TiptapCollabProvider = enabled
export type CollabProviderState = TiptapCollabProvider | undefined;

// The initial ydoc config state snapshot for comparison usage
// object can be the NxtYDocConfig obj if ydoc had it previously
// null - default state, waiting for onSync event to set it
// consider null as undetermined or not ready, meaning we cant proceed to setup editors
// once synced and a snapshot is checked, it will be an empty object or the actual config object
// it can be an empty object if the ydoc was never set with a config (eg first time opening in collab)
export type SyncedInitialYDocConfigState = NxtYDocConfig | null;

// key for the config map in the ydoc
export const NXT_YDOC_CONFIG = 'nxt_ydoc_config';

/**
 * Common Utility Types
 */
export type TAssetFormSetValue = ReturnType<
  typeof useFormContext<AssetFormValuesType>
>['setValue'];
export type TAssetFormGetValues = ReturnType<
  typeof useFormContext<AssetFormValuesType>
>['getValues'];
export type TSyncStoreState = AssetEditorsContextValuesType['nxtSyncStore']['state'];
export type YDocType = AssetConfigContextValuesType['yDoc'];
export type TReadOnlyMode = AssetConfigContextValuesType['isReadOnlyMode'];

/**
 * Read Only Mode State
 * isReadOnly - whether the asset is in read-only mode
 * message - message to show in the UI explaining read-only state
 * reason - the reason for read-only state, eg: 'lockout_view' | 'permissions' | null
 * eg: set lockout_view if a user chooses to view in read-only mode after being
 * presented with the "asset is locked" interstitial screen.
 */
export type ReadOnlyState = {
  isReadOnly: boolean;
  message: string;
  reason: null | 'lockout_view' | 'permissions' | 'deleted';
};

export const initReadOnlyState: ReadOnlyState = {
  isReadOnly: false,
  message: '',
  reason: null,
};

/**
 * Asset Lock Check Helpers.
 */
export function isAssetLocked(
  assetLockData?: GetEditorialAssetIdLockResult['data'],
): boolean {
  const check = assetLockData?.locked === true;
  return check;
}

export function isAssetLockedByNXT(
  assetLockData?: GetEditorialAssetIdLockResult['data'],
): boolean {
  const check = assetLockData?.origin === NXT_LOCK_ORIGIN;
  return check;
}

export function isAssetLockedByUser(
  assetLockData?: GetEditorialAssetIdLockResult['data'],
  userUuid?: string,
): boolean {
  if (!userUuid || !assetLockData?.user?.uuid) {
    return false; // unable to compare
  }
  const check = assetLockData?.user?.uuid === userUuid;
  return check;
}

/**
 * Resolve prefill values for create new asset.
 * Pass result to base createAssetDefaultValues.
 */

type ResolveAssetPrefillValuesArgs = {
  globalSettings?: Partial<GlobalSettingsType>;
  userInfo?: Partial<GetUserMeData>;
  siteSettings?: Partial<SiteSettingsType>;
  siteWorkflows?: Workflow[];
  createAssetPrefill?: AssetQuickLaunchPrefill;
  disablePhotoSales?: string | null;
  assetType?: string;
};

type ResolveAssetPrefillValuesResult = Partial<CreateAssetDefaultValuesArgs>;

export function resolveAssetPrefillValues({
  globalSettings,
  userInfo,
  siteSettings,
  siteWorkflows,
  createAssetPrefill,
  disablePhotoSales,
  assetType,
}: ResolveAssetPrefillValuesArgs): ResolveAssetPrefillValuesResult {
  const defaultAuthor =
    siteSettings?.user?.default_author ?? globalSettings?.user?.default_author;
  const authors = defaultAuthor
    ? [
        {
          id: userInfo?.uuid,
          uuid: userInfo?.uuid,
          account_type: userInfo?.account_type,
          email: userInfo?.email,
          screen_name: userInfo?.screen_name,
          avatar: userInfo?.avatar,
          first_name: userInfo?.first_name,
          last_name: userInfo?.last_name,
          title: userInfo?.title,
          department: userInfo?.department,
          create_time: userInfo?.create_time,
          ltv: userInfo?.ltv,
        },
      ]
    : [];

  // Has workflow set from quick launch modal?
  const workflowOverride = createAssetPrefill?.workflowId;

  const workflowFromOverride =
    workflowOverride === undefined ? undefined : normalizeWorkflowId(workflowOverride);

  const siteWfPreference =
    normalizeWorkflowId(siteSettings?.user?.default_workflow) ?? undefined;

  const siteWorkflowProcessPreference =
    normalizeWorkflowId(siteSettings?.user?.default_workflow_process) ?? undefined;

  const workflowId =
    workflowFromOverride !== undefined ? workflowFromOverride : siteWfPreference;

  // No override: user profile setings preference
  // Has override: look up workflow and pick first process id
  // Null override (cleared) no process either
  let wfProcessDefaultId: string | null | undefined;
  if (workflowFromOverride === undefined) {
    wfProcessDefaultId = siteWorkflowProcessPreference;
  } else if (workflowFromOverride && typeof workflowFromOverride === 'string') {
    const matchedWorkflow = siteWorkflows?.find((wf) => wf?.id === workflowFromOverride);
    const firstProcId = matchedWorkflow?.processes?.[0]?.id ?? undefined;
    wfProcessDefaultId = firstProcId;
  } else {
    wfProcessDefaultId = undefined;
  }

  const startTimeOverride = createAssetPrefill?.startTime;
  const siteStartTime =
    siteSettings?.user?.default_start_time ?? globalSettings?.user?.default_start_time;
  const preferenceStartTime = dayjs(siteStartTime).isValid()
    ? (siteStartTime ?? undefined)
    : undefined;
  const startTime =
    startTimeOverride !== undefined ? startTimeOverride : preferenceStartTime;

  const sanitizedSlug = createAssetPrefill?.slug?.trim() ?? '';
  const prefillTitle = createAssetPrefill?.title?.trim() ?? '';

  // User's default presentation preference (site-scoped) for the asset type being created.
  // Only article/image/collection support presentation; other types stay null.
  const defaultPresentations = siteSettings?.user?.default_presentations;
  const rawDefaultPresentation =
    assetType && defaultPresentations
      ? defaultPresentations[assetType as keyof typeof defaultPresentations]
      : undefined;
  const presentation =
    typeof rawDefaultPresentation === 'string' && rawDefaultPresentation.length
      ? rawDefaultPresentation
      : null;

  // disablePhotoSales truthy → default to 'disable'; falsy/null → leave unset (automatic)
  const photoSales: 'disable' | undefined = disablePhotoSales ? 'disable' : undefined;

  return {
    authors: authors,
    startTime: startTime,
    workflowId: workflowId ?? null,
    workflowProcessId: wfProcessDefaultId ?? null,
    slug: sanitizedSlug,
    title: prefillTitle,
    photoSales: photoSales,
    presentation: presentation,
  };
}

/**
 * Create form default values for editorial asset.
 * Pass in optional defaults for certain fields.
 */
export type CreateAssetDefaultValuesArgs = {
  type: AssetType | string;
  title?: string;
  authors?: UserAccountSummary[];
  startTime?: string | null;
  workflowId?: string | null;
  workflowProcessId?: string | null;
  slug?: string | null | undefined;
  photoSales?: 'enable' | 'disable';
  presentation?: string | null;
  isTemplate?: boolean;
};

export function createAssetDefaultValues({
  type,
  title,
  authors,
  startTime,
  workflowId,
  workflowProcessId,
  slug,
  photoSales,
  presentation,
  isTemplate,
}: CreateAssetDefaultValuesArgs) {
  // CMSADMIN-1623 - properties.new_window
  // create new links default true in new window
  const isLinkType = type === 'link';

  const isCollectionType = type === 'collection';

  const createDefaultValues: Partial<FullEditorialAsset> = {
    type: type,
    title: title ?? '',
    content: '',
    summary: '',
    byline: '',
    authors: authors ?? [],
    // Dates
    start_time: startTime ?? null,
    archive_time: null,
    delete_time: null,
    keywords: [],
    flags: [],
    site_tags: [],
    sections: [],
    children: [],
    siblings: [],
    parents: [],
    related_content: [],
    custom: {},
    draft: true,
    subtype: '',
    properties: {
      app: 'editorial', // 'source application'
      // Publish checkbox - default no publish
      no_publish: 1,
      hammer: '',
      kicker: '',
      subheadline: '',
      tagline: '',
      priority: null,
      source: '',
      html: '',
      link: '',
      // Alt text
      alttext: '',
      // Location
      country: null,
      region: null,
      address: '',
      postal_code: '',
      municipality: '',
      latitude: null,
      longitude: null,
      // SEO / Social
      seo_title: '',
      seo_description: '',
      url_title: '',
      canonical_url: '',
      social_title: '',
      social_summary: '',
      social_twitter_card: '',
      presentation: presentation ?? null,
      article_type: null,
      // Print
      print_ready: false,
      print_headline: '',
      print_jump_head: '',
      print_jump_keyword: '',
      print_notes: '',
      print_placement_priority: null,
      print_slug: slug ?? '',
      print_subheadline: '',
      // Properties
      no_reactions: false,
      no_comments: false,
      photo_sales: photoSales,
      // Workflow
      workflow_id: workflowId ?? null,
      workflow_process_id: workflowProcessId ?? null,
      // Cover art
      cover_align_horizontal: null,
      cover_align_vertical: null,
      cover_text_color: null,
      cover_text_background: null,
      cover_effect: null,
      // Dynamic Collections
      sort_rules: [],
      search_rules: [],
      results: null,
    },
  };

  // CMSADMIN-1623 - create links default to open in new window
  if (isLinkType && createDefaultValues.properties) {
    createDefaultValues.properties.new_window = true;
  }

  // CMSADMIN-2580 - new template flow stamps template:true at creation
  if (isTemplate) {
    createDefaultValues.template = true;
  }

  // CMSADMIN-2362 - default to static 'curated' collection
  if (isCollectionType && createDefaultValues.properties) {
    createDefaultValues.subtype = 'static';
    createDefaultValues.properties.collection_mode = 'static';
  }

  return createDefaultValues;
}

/**
 * Helper to setup the AssetForm default values.
 * AutoSave and RHF will compare against these
 * default values to detect form dirty state, so
 * when adding new fields this should match what
 * those inputs would setValue as upon mount/load.
 *
 * also note anything inside of properties
 * eg hammer, kicker, subheadline, etc
 * these keys may or may not exist on record so the
 * the k:v can be undefined, it is dependent on
 * if the asset was ever saved with these properties.
 *
 * with that in mind since RHF will do a strict equality check
 * if properties?.something is undefined (or really any field)
 * but our component setValue as '' or null, RHF will consider
 * this field dirty and the autosave will detect it as dirty.
 *
 * So when adding new fields define the default value here
 * and it should should what the input would set to the form,
 * while falling back ?? to what the field would be if empty.
 *
 * See also:
 * https://react-hook-form.com/docs/useform/reset
 * https://react-hook-form.com/docs/useform/resetfield
 *
 */

export function formatAssetFormDefaultValues(
  record?: Partial<FullEditorialAsset>,
): AssetFormValuesType | undefined {
  if (!record) {
    console.warn('[formatAssetFormDefaultValues] - missing record');
    return undefined;
  }
  // copy to avoid mutating original obj
  const original: Partial<FullEditorialAsset> = clone(record);

  // set BaseUserAcctSummary[] to [{uuid:'...'}] for payload / form defaults
  const authorsPayload = getAuthorsUuidArray(original?.authors);

  // CMSADMIN-1336 - removes any 'custom_' prefixed keys in properties.
  const cleanProperties = cleanCustomProperties(original?.properties);

  const formatted: AssetFormValuesType = {
    title: original?.title ?? '',
    content: original?.content ?? '',
    byline: original?.byline ?? '',
    summary: original?.summary ?? '',
    keywords: original?.keywords ?? [],
    flags: original?.flags ?? [],
    site_tags: original?.site_tags ?? [],
    sections: original?.sections ?? [],
    children: original?.children ?? [],
    siblings: original?.siblings ?? [],
    parents: original?.parents ?? [],
    related_content: original?.related_content ?? [],
    authors: authorsPayload,
    start_time: original?.start_time ?? null,
    delete_time: original?.delete_time ?? null,
    archive_time: original?.archive_time ?? null,
    draft: original?.draft ?? true,
    subtype: original?.subtype ?? '',
    custom_data: original?.custom_data ?? {},
    custom: original?.custom ?? {},
    properties: {
      ...cleanProperties,
      abuse: original?.properties?.abuse,
      hammer: original?.properties?.hammer ?? '',
      kicker: original?.properties?.kicker ?? '',
      subheadline: original?.properties?.subheadline ?? '',
      tagline: original?.properties?.tagline ?? '',
      priority: original?.properties?.priority ?? null,
      source: original?.properties?.source ?? '',
      html: original?.properties?.html ?? '',
      link: original?.properties?.link ?? '',
      // Alt text
      alttext: original?.properties?.alttext ?? '',
      // Location
      country: original?.properties?.country ?? null,
      region: original?.properties?.region ?? null,
      address: original?.properties?.address ?? '',
      postal_code: original?.properties?.postal_code ?? '',
      municipality: original?.properties?.municipality ?? '',
      latitude: original?.properties?.latitude ?? null,
      longitude: original?.properties?.longitude ?? null,
      // SEO / Social
      seo_title: original?.properties?.seo_title ?? '',
      seo_description: original?.properties?.seo_description ?? '',
      url_title: original?.properties?.url_title ?? '',
      canonical_url: original?.properties?.canonical_url ?? '',
      social_title: original?.properties?.social_title ?? '',
      social_summary: original?.properties?.social_summary ?? '',
      social_twitter_card: original?.properties?.social_twitter_card ?? '',
      presentation: original?.properties?.presentation ?? null,
      article_type: original?.properties?.article_type ?? null,
      // Print
      print_ready: original?.properties?.print_ready ?? false,
      print_headline: original?.properties?.print_headline ?? '',
      print_jump_head: original?.properties?.print_jump_head ?? '',
      print_jump_keyword: original?.properties?.print_jump_keyword ?? '',
      print_notes: original?.properties?.print_notes ?? '',
      print_placement_priority: original?.properties?.print_placement_priority ?? null,
      print_slug: original?.properties?.print_slug ?? '',
      print_subheadline: original?.properties?.print_subheadline ?? '',
      // Publish checkbox
      no_publish: original?.properties?.no_publish ?? null,
      // Properties
      no_reactions: original?.properties?.no_reactions ?? false,
      no_comments: original?.properties?.no_comments ?? false,
      photo_sales: original?.properties?.photo_sales,
      // Workflow
      workflow_id: original?.properties?.workflow_id ?? null,
      workflow_process_id: original?.properties?.workflow_process_id ?? null,
      // Cover art
      cover_align_horizontal: original?.properties?.cover_align_horizontal ?? null,
      cover_align_vertical: original?.properties?.cover_align_vertical ?? null,
      cover_text_color: original?.properties?.cover_text_color ?? null,
      cover_text_background: original?.properties?.cover_text_background ?? null,
      cover_effect: original?.properties?.cover_effect ?? null,
      // Dynamic Collections
      collection_mode: original?.properties?.collection_mode ?? '',
      sort_rules: original?.properties?.sort_rules ?? [],
      search_rules: original?.properties?.search_rules ?? [],
      results: original?.properties?.results ?? null,
    },
  };

  // https://remedajs.com/docs/#mergeDeep
  const merged: AssetFormValuesType = mergeDeep(original, formatted);

  return merged;
}
